/**
 * Echo guard: correlates the microphone energy envelope with the assistant playback envelope.
 * A high correlation while the assistant is speaking means the "speech" the VAD saw is the
 * assistant's own voice leaking into the mic (speakers, no headset).
 */
export class EchoGuard {
  private mic: number[] = [];
  private out: number[] = [];
  private readonly window: number;
  private chronicHits = 0;
  private chronicTotal = 0;
  lastCorrelation = 0;

  constructor(windowFrames = 16) {
    this.window = windowFrames;
  }

  push(micRms: number, outRms: number) {
    this.mic.push(micRms);
    this.out.push(outRms);
    if (this.mic.length > this.window) {
      this.mic.shift();
      this.out.shift();
    }
    this.lastCorrelation = this.correlation();
  }

  correlation(): number {
    const n = this.mic.length;
    if (n < 6) return 0;
    const ma = this.mic.reduce((s, v) => s + v, 0) / n;
    const mb = this.out.reduce((s, v) => s + v, 0) / n;
    let num = 0,
      da = 0,
      db = 0;
    for (let i = 0; i < n; i++) {
      const a = this.mic[i] - ma;
      const b = this.out[i] - mb;
      num += a * b;
      da += a * a;
      db += b * b;
    }
    if (da < 1e-9 || db < 1e-9) return 0;
    return num / Math.sqrt(da * db);
  }

  /** Decide whether an onset is self-speech; also tracks a chronic ratio for the headset hint. */
  isEcho(threshold: number, playbackActive: boolean): boolean {
    if (!playbackActive) return false;
    const echo = this.lastCorrelation > threshold;
    this.chronicTotal++;
    if (echo) this.chronicHits++;
    if (this.chronicTotal > 40) {
      this.chronicTotal = Math.floor(this.chronicTotal / 2);
      this.chronicHits = Math.floor(this.chronicHits / 2);
    }
    return echo;
  }

  get chronic(): boolean {
    return this.chronicTotal >= 8 && this.chronicHits / this.chronicTotal > 0.5;
  }

  /** Clear the correlation window only (e.g. after ducking, so bleed history does not bias the next frames). */
  resetWindow() {
    this.mic = [];
    this.out = [];
    this.lastCorrelation = 0;
  }

  /** Full reset: correlation window and the chronic-echo counters (new turn / playback stopped). */
  reset() {
    this.resetWindow();
    this.chronicHits = 0;
    this.chronicTotal = 0;
  }
}

/** Slow-tracking noise floor estimate (RMS) using asymmetric smoothing. */
export class NoiseFloor {
  value = 0.005;
  update(rmsValue: number, speaking: boolean) {
    if (speaking) return;
    const a = rmsValue < this.value ? 0.2 : 0.01;
    this.value = this.value + a * (rmsValue - this.value);
  }
}
